import { useCurrencyConverter } from "../Hooks/useCurrencyConverter";

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}


export function usePriceNGN() {
	const { convert, format, rateMap, baseCurrency, loading } =
		useCurrencyConverter();

	const fmtNGN = (usdAmount) => {
		const amt = parseFloat(usdAmount);
		if (!usdAmount || isNaN(amt) || amt === 0) return "₦ —";
		if (baseCurrency === "NGN") return format(amt, "NGN");
		const ngn = rateMap["NGN"];
		if (!ngn?.hasRate) {
			return format(amt, baseCurrency);
		}
		const ngnVal = convert(amt, baseCurrency, "NGN");
		return ngnVal !== null ? format(ngnVal, "NGN") : format(amt, baseCurrency);
	};

	return { fmtNGN, loading, hasNGN: !!rateMap["NGN"]?.hasRate };
}

// export function useNGNDisplay() {
//   const { convert, format, rateMap, baseCurrency } = useCurrencyConverter();
//   const toNGN = (usdAmount) => {
//     const amt = parseFloat(usdAmount);
//     if (!amt || isNaN(amt)) return "";
//     if (baseCurrency === "NGN") return String(amt);
//     const ngn = rateMap["NGN"];
//     if (!ngn?.hasRate) return String(amt);  
//     const result = convert(amt, baseCurrency, "NGN");
//     return result !== null ? result.toFixed(2) : String(amt);
//   };
//   return { toNGN };
// }

export function useNGNtoUSD() {
  const { convert, rateMap, baseCurrency, loading } = useCurrencyConverter();
  const toUSD = (ngnAmount) => {
    const amt = parseFloat(ngnAmount);
    if (!amt || isNaN(amt)) return 0;
    if (baseCurrency === "NGN") return amt;  
    const ngn = rateMap["NGN"];
    if (!ngn?.hasRate) return amt;  
    const result = convert(amt, "NGN", baseCurrency);
    return result !== null ? parseFloat(result.toFixed(4)) : amt;
  };
  return {
    toUSD,
    ratesLoading: loading,
    ratesReady: !!rateMap["NGN"]?.hasRate,
  };
}

export function formatCurrency(
  value,
  minFD = 2,
  maxFD = 2,
  currency
) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency ?? 'NGN',
    maximumFractionDigits: maxFD,
    minimumFractionDigits: minFD,
  }).format(value);
}

export function generateSubcategoriesArray(categories) {
  const flattenedCategories = [];

  function processCategory(category, parentName = null, rootName = null) {
    // Determine the root category name
    const rootCategory = rootName || category.name;

    if (category.sub_categories && category.sub_categories.length > 0) {
      category.sub_categories.forEach((subCategory) => {
        const transformedCategory = {
          id: subCategory.id,
          category_id: subCategory.category_id,
          name: subCategory.name,
          image: subCategory.image,
          description: subCategory.description,
          parent_category: category.name,
          parent_id: subCategory.parent_id,
          root_category: rootCategory,
          createdAt: subCategory.createdAt,
          updatedAt: subCategory.updatedAt,
        };

        // Add the transformed category to the result
        flattenedCategories.push(transformedCategory);

        // Recursively process nested sub-categories
        processCategory(subCategory, subCategory.name, rootCategory);
      });
    }
  }

  // Process each main category
  categories.forEach((category) => {
    processCategory(category);
  });

  return flattenedCategories;
}

export const formatString = (str) => str.replaceAll('_', ' ');

export const addCommasToNumber = (number) => {
  if (!number && number !== 0) return;
  if (number === 0) return 0;
  number = roundTo2DP(number).toString();
  if (number.includes('.')) {
    const parts = number.split('.');
    return (
      parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
      '.' +
      parts.slice(1).join('')
    );
  }
  return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const roundTo2DP = (num) => {
  // if (isNaN(num)) return;
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Parses the "reason" field from salesreturn.
 */
export function parseReason(reason) {
  // Case 2: plain reason (no ":" present → simple text)
  if (!reason.includes(':')) {
    return { return_reason: reason.trim() };
  }

  // Case 1: structured reason string
  const parsed = {};
  const productData = {};

  // Split on commas, but keep key-value pairs clean
  const parts = reason.split(',').map((p) => p.trim());

  parts.forEach((part) => {
    const [rawKey, ...rawValParts] = part.split(':');
    const key = rawKey?.trim();
    const value = rawValParts.join(':').trim();

    if (!key) return;

    switch (key) {
      case 'return_reason':
        parsed.return_reason = value;
        break;
      case 'comment':
        parsed.comment = value;
        break;
      case 'order_ref':
        parsed.order_ref = value;
        break;
      default:
        // If the part looks like productData sub-field
        productData[key] = value;
    }
  });

  if (Object.keys(productData).length > 0) {
    parsed.productData = productData;
  }

  return parsed;
}

export function getSymbolFromCurrency(stringifiedCurrency) {
  if (!stringifiedCurrency) return undefined;
  let currency = stringifiedCurrency;

  // Keep parsing if it's a JSON-looking string
  while (typeof currency === 'string') {
    try {
      const parsed = JSON.parse(currency);
      currency = parsed;
    } catch {
      break; // stop if it fails
    }
  }

  // If we ended up with an object with symbol
  if (currency && typeof currency === 'object' && currency.symbol) {
    currency = currency.symbol;
  }

  // If it's still quoted string (e.g. "\"NGN\"")
  if (typeof currency === 'string' && /^".+"$/.test(currency)) {
    try {
      currency = JSON.parse(currency);
    } catch {
      currency = undefined;
    }
  }

  // Final cleanup
  if (!currency || typeof currency !== 'string') {
    currency = undefined;
  }

  return currency;
}
export function parseCurrency(raw) {
  let currency = raw;

  // Keep parsing while it's a JSON string
  while (typeof currency === 'string') {
    try {
      const parsed = JSON.parse(currency);
      currency = parsed;
    } catch {
      break;
    }
  }

  // If object with symbol (and maybe rate)
  if (currency && typeof currency === 'object' && currency.symbol) {
    return {
      symbol: String(currency.symbol || '') ?? undefined,
      rate: typeof currency.rate === 'number' ? currency.rate : undefined,
    };
  }

  // Handle quoted plain string like "\"NGN\""
  if (typeof currency === 'string' && /^".+"$/.test(currency)) {
    try {
      currency = JSON.parse(currency);
    } catch {
      return { symbol: undefined, rate: undefined };
    }
  }

  // If final result is just a string (e.g. "USD")
  if (typeof currency === 'string') {
    return { symbol: currency, rate: undefined };
  }

  // Fallback
  return { symbol: undefined, rate: undefined };
}

export const getCurrencyAndRate = (order) => {
    const currency =
        order?.currency && !order?.currency?.includes('{}') && order?.currency?.includes('{')
            ? JSON.parse(order?.currency)?.symbol ?? JSON.parse(order?.currency)
            : order?.currency && typeof order?.currency === 'string'
            ? order?.currency
            : 'USD';
    const currencyRate =
        order?.currency && !order?.currency.includes('{}') && order?.currency?.includes?.('rate')
            ? JSON.parse(order?.currency)?.rate ?? JSON.parse(order?.currency)
            : '1';
    const finalCurrency =
        typeof currency === 'string' && currency?.includes('{')
            ? JSON.parse(currency)?.symbol
            : currency;
    const finalRate =
        isNaN(currencyRate) && currencyRate?.includes('{')
            ? JSON.parse(currencyRate)
            : currencyRate || 1;
    return { finalCurrency, finalRate };
};